import type { SanityPageBuilderChipEvolutionGraphBulletPoint, SanityPageBuilderChipEvolutionGraphItem, SanityPageBuilderChipEvolutionGraphSection, SanityPageBuilderSection } from "@ouster/shared";
import { ChipEvolutionGraphBlockView } from "~/features/blocks/inventory/chip-evolution-graph";
import { ChipEvolutionGraphBulletPointsView } from "~/features/blocks/inventory/chip-evolution-graph/chip-evolution-graph-bullet-points";
import { ChipEvolutionGraphBulletPointView } from "~/features/blocks/inventory/chip-evolution-graph/chip-evolution-graph-bullet-points/chip-evolution-graph-bullet-point";
import { ChipEvolutionGraphItemView } from "~/features/blocks/inventory/chip-evolution-graph/chip-evolution-graph-item";
import { GraphView } from "~/features/blocks/inventory/chip-evolution-graph/graph";
import { GraphLineView } from "~/features/blocks/inventory/chip-evolution-graph/graph-line";
import { hasItems } from "~/features/app/utils";
import { Asset, type Props as AssetProps } from "~/features/craft/asset";
import { toCraftAsset } from "~/features/sanity/media";
import { toCraftLinkArray } from "~/features/sanity/link";
import { SanityRichText } from "~/features/sanity/rich-text";
import type { Mode, Theme } from "~/features/ui/get-theme-style";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderChipEvolutionGraphSection;
  };
};
export function SanityChipEvolutionGraphSection({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const mode = (sectionSettings?.mode || "light") as Mode;
  const theme = (sectionSettings?.theme || "white") as Theme;
  const showXAxisData = sectionContent.showXAxisData ?? false;
  const items = sectionContent.items ?? [];
  const bulletPoints = sectionContent.bulletPoints ?? [];
  const text = sectionContent.text?.length ? <SanityRichText value={sectionContent.text} /> : undefined;
  return <ChipEvolutionGraphBlockView anchor={{
    id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
    name: sectionSettings?.anchorName
  }} bulletPoints={<ChipEvolutionGraphBulletPointsView<SanityPageBuilderChipEvolutionGraphBulletPoint> points={bulletPoints} theme={theme} mode={mode} getPointKey={(point, index) => point._key || index} renderPoint={point => <ChipEvolutionGraphBulletPointView text={point.text} />} />} hasGraph={hasItems(items)} hasTopDivider={sectionSettings?.hasTopDivider ?? true} headline={sectionContent.headline} link={toCraftLinkArray(sectionContent.link)} linkText={sectionContent.linkText} mode={mode} renderGraph={() => <GraphView<SanityPageBuilderChipEvolutionGraphItem> items={items} theme={theme} mode={mode} getItemId={(item, index) => item._key || index} getItemValue={item => (item.range ?? 0) * (item.pointsPerSecond ?? 0)} getItemKey={(item, index) => item._key || index} renderItem={(item, index, {
    animate,
    maxValue
  }) => <ChipEvolutionGraphItemView id={item._key} media={<Asset asset={toCraftAsset(item.image) as AssetProps["asset"]} imageProps={{
    width: 100,
    fit: "cover"
  }} />} name={item.name} year={item.year} range={item.range} pointsPerSecond={item.pointsPerSecond} theme={theme} mode={mode} animate={animate} index={index} maxValue={maxValue} showXAxisData={showXAxisData} />} renderLine={({
    animate,
    index,
    containerRef,
    startId,
    endId
  }) => <GraphLineView animate={animate} index={index} containerRef={containerRef} startId={startId} endId={endId} mode={mode} />} />} sectionProps={{
    "data-sanity-section-key": section._key,
    "data-sanity-section-type": section._type
  }} text={text} theme={theme} />;
}